import type { ReactNode } from 'react';

interface AsyncContentProps {
  /** Whether the data is still loading. */
  loading: boolean;
  /** An error message to display instead of children. Pass `null` / `undefined` for no error. */
  error?: string | null;
  /** Content to render when not loading and there is no error. */
  children: ReactNode;
  /** Override the loading text. Defaults to "Loading...". */
  loadingMessage?: string;
}

/**
 * Renders a loading spinner, error banner, or the children based on
 * the current async state.
 *
 * Uses the same CSS classes found throughout the codebase
 * (`loading-spinner`, `clinic-list-loading`, etc.) so it blends in
 * without requiring new styles.
 */
export default function AsyncContent({
  loading,
  error,
  children,
  loadingMessage = 'Loading...',
}: Readonly<AsyncContentProps>) {
  if (loading) {
    return (
      <div className="panel-status text-center p-8">
        <div className="loading-spinner" />
        <p>{loadingMessage}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-status error p-4 text-red-600">
        {error}
      </div>
    );
  }

  return <>{children}</>;
}
